import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, KeyRound, DoorClosed, RefreshCw, User, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_META = {
  activa:        { label: 'Disponible',    dot: 'bg-emerald-500', card: 'bg-emerald-50 border-emerald-300 text-emerald-900' },
  disponible:    { label: 'Disponible',    dot: 'bg-emerald-500', card: 'bg-emerald-50 border-emerald-300 text-emerald-900' },
  aseo:          { label: 'En aseo',       dot: 'bg-amber-400',   card: 'bg-amber-50 border-amber-300 text-amber-900' },
  mantenimiento: { label: 'Mantenimiento', dot: 'bg-orange-500',  card: 'bg-orange-50 border-orange-300 text-orange-900' },
  deshabilitada: { label: 'Deshabilitada', dot: 'bg-slate-400',   card: 'bg-slate-100 border-slate-300 text-slate-700' },
};

export default function SgQrHabitacion() {
  const { numero } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Flujo 2 pasos
  const [step, setStep] = useState(1); // 1: clave · 2: acción
  const [password, setPassword] = useState('');
  const [validating, setValidating] = useState(false);
  const [personal, setPersonal] = useState([]);
  const [personalId, setPersonalId] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState('aseo');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [confirmado, setConfirmado] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/servicios-generales/qr/habitacion/${encodeURIComponent(numero)}`);
      setData(r.data);
      if (r.data.estados_permitidos?.length) {
        setNuevoEstado(prev => r.data.estados_permitidos.includes(prev) ? prev : r.data.estados_permitidos[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Habitación no encontrada');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [numero]);

  useEffect(() => { load(); }, [load]);

  const validarClave = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Ingresa la clave'); return; }
    setValidating(true);
    try {
      const r = await axios.post(`${API}/servicios-generales/qr/validar-clave`, { password });
      setPersonal(r.data.personal || []);
      if ((r.data.personal || []).length === 0) {
        toast.error('No hay personal de soporte registrado. Contacta al administrador.');
        return;
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clave incorrecta');
    } finally {
      setValidating(false);
    }
  };

  const cerrar = () => {
    setStep(1); setPassword(''); setPersonalId(''); setNota('');
    setPersonal([]);
  };

  const submitEstado = async (e) => {
    e.preventDefault();
    if (!personalId) { toast.error('Selecciona quién realizó la acción'); return; }
    const persona = personal.find(p => String(p.id) === String(personalId));
    setEnviando(true);
    setConfirmado(null);
    try {
      const r = await axios.post(
        `${API}/servicios-generales/qr/habitacion/${encodeURIComponent(numero)}/estado`,
        {
          password,
          estado: nuevoEstado,
          operador_nombre: persona ? `${persona.nombre}${persona.rut ? ' · ' + persona.rut : ''}` : '',
          nota,
        }
      );
      setConfirmado({ estado: r.data.estado, hora: new Date(), persona: persona?.nombre });
      toast.success(`Estado actualizado a "${r.data.estado}".`);
      setNota('');
      load();
      // Auto cierre de sesión de la clave tras 30s para forzar re-autenticación del siguiente operador
      setTimeout(() => cerrar(), 30 * 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-800 flex items-center justify-center p-4">
        <div className="text-white text-sm">Cargando…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-700 to-red-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center">
          <h1 className="text-lg font-bold text-red-800 mb-2">Habitación no encontrada</h1>
          <p className="text-sm text-slate-500">El código QR no corresponde a una habitación registrada.</p>
        </div>
      </div>
    );
  }

  const estadoActual = data.ocupantes_actuales > 0 ? 'ocupada' : data.estado;
  const meta = ESTADO_META[estadoActual] || ESTADO_META.activa;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-800 py-6 px-4 flex items-start justify-center">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="bg-white rounded-t-2xl px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center">
              <DoorClosed className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Servicios Generales · Hotelería</div>
              <div className="text-2xl font-black text-slate-900 leading-tight">Habitación {data.numero}</div>
              <div className="text-xs text-slate-500">{data.edificio || 'Sin edificio'}{data.tipo ? ' · ' + data.tipo : ''}</div>
            </div>
          </div>
        </div>

        {/* Estado actual */}
        <div className={`px-5 py-4 border border-t-0 border-slate-100 ${meta.card}`}>
          <div className="text-[11px] font-bold uppercase tracking-widest opacity-75">Estado actual</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`w-3 h-3 rounded-full ${meta.dot}`} />
            <span className="text-xl font-black">{data.ocupantes_actuales > 0 ? 'Ocupada' : meta.label}</span>
          </div>
        </div>

        {/* Confirmación reciente */}
        {confirmado && (
          <div className="bg-emerald-600 text-white px-5 py-3 flex items-start gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Estado actualizado a <b>{confirmado.estado}</b> a las {confirmado.hora.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
              {confirmado.persona && <div className="text-[11px] opacity-90">por {confirmado.persona}</div>}
            </div>
          </div>
        )}

        {step === 1 ? (
          /* Paso 1 · Clave */
          <form onSubmit={validarClave} className="bg-white rounded-b-2xl px-5 py-5 space-y-4 shadow-2xl">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
                <KeyRound className="w-3 h-3" /> Clave de acceso
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="off"
                autoFocus placeholder="Ingresa la clave"
                className="w-full px-3 py-3 text-lg border border-slate-300 rounded-lg font-mono tracking-widest text-center"
                data-testid="qr-password" />
              <p className="text-[10px] text-slate-400 mt-1">
                Sólo personal autorizado puede cambiar el estado. Consulta la clave con tu supervisor.
              </p>
            </div>
            <button type="submit" disabled={validating || !password}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold rounded-lg disabled:opacity-50"
              data-testid="qr-ingresar">
              {validating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {validating ? 'Validando…' : 'Ingresar'}
            </button>
          </form>
        ) : (
          /* Paso 2 · Selección + estado */
          <form onSubmit={submitEstado} className="bg-white rounded-b-2xl px-5 py-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Clave válida</span>
              <button type="button" onClick={cerrar}
                className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="qr-salir">
                <LogOut className="w-3 h-3" /> Salir
              </button>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
                <User className="w-3 h-3" /> ¿Quién realiza la acción? *
              </label>
              <select value={personalId} onChange={e => setPersonalId(e.target.value)}
                className="w-full px-3 py-3 text-sm border border-slate-300 rounded-lg bg-white"
                data-testid="qr-select-personal" required>
                <option value="">— Selecciona una persona —</option>
                {personal.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} · {p.cargo || 'Sin cargo'}{p.empresa ? ' · ' + p.empresa : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">
                Sólo personal registrado en Mantenedores → Personal de Soporte.
              </p>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nuevo estado</label>
              <div className="grid grid-cols-3 gap-2">
                {data.estados_permitidos.map(estKey => {
                  const m = ESTADO_META[estKey] || {};
                  const sel = nuevoEstado === estKey;
                  return (
                    <button key={estKey} type="button" onClick={() => setNuevoEstado(estKey)}
                      className={`px-2 py-2 rounded-lg border-2 text-xs font-bold uppercase tracking-wide transition-all ${sel ? (m.card || 'bg-emerald-50 border-emerald-500') + ' ring-2 ring-emerald-500' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid={`qr-estado-${estKey}`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${m.dot || 'bg-slate-400'}`} />
                      {m.label || estKey}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nota (opcional)</label>
              <input type="text" value={nota} onChange={e => setNota(e.target.value)} maxLength={140}
                placeholder="Observación breve…"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                data-testid="qr-nota" />
            </div>

            <button type="submit" disabled={enviando || !personalId}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold rounded-lg disabled:opacity-50"
              data-testid="qr-submit">
              {enviando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {enviando ? 'Enviando…' : 'Confirmar cambio de estado'}
            </button>
          </form>
        )}

        <p className="text-center text-[10px] text-white/70 mt-4">
          © TystaCore · Compañía Minera Lomas Bayas
        </p>
      </div>
    </div>
  );
}
